import { useNavigate } from '@tanstack/react-router'
import { trpc } from '@viastud/ui/lib/trpc'
import { FreeLessonModal } from '@viastud/ui/shared/free-lesson-modal'
import { GenericModal } from '@viastud/ui/shared/generic-modal'
import { StudentProgressOverview } from '@viastud/ui/shared/task-recommendation/student-progress-overview'
import { ViastudRulesModal } from '@viastud/ui/shared/viastud-rules-modal'
import { TrendingUp } from 'lucide-react'
import { useState } from 'react'

import { useStudentDashboardPresenter } from '@/presenters/student-dashboard.presenter'
import { useAuthStore } from '@/store/auth.store.ts'

import { StudentCoursesCard } from './student-courses-card'
import { StudentDashboardOverviewCard } from './student-dashboard-overview-card'

interface StudentStats {
  chapterProgress?: number
  averageQuizGrade?: number | null
  reservedCoursesCount?: number
  hasQuizDone?: boolean
  performancePercentile?: number | null
}

export default function DashboardComponent({ showPopup }: { showPopup: boolean }) {
  const { user: student } = useAuthStore()
  const { data: subscription } = trpc.user.getStudentSubscriptionDetails.useQuery()
  const { data: testSession } = trpc.module.getSessionTest.useQuery()
  const [hasShowPopUp, setHasShowPopUp] = useState<boolean>(showPopup)
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false)
  const presenter = useStudentDashboardPresenter()
  const navigate = useNavigate()

  // Fonction helper pour accéder aux statistiques de manière sécurisée
  const getStudentStats = (): StudentStats | undefined => {
    if (!presenter.studentStats) return undefined

    const stats = presenter.studentStats as StudentStats
    return {
      chapterProgress:
        typeof stats.chapterProgress === 'number' ? stats.chapterProgress : undefined,
      averageQuizGrade: typeof stats.averageQuizGrade === 'number' ? stats.averageQuizGrade : null,
      reservedCoursesCount:
        typeof stats.reservedCoursesCount === 'number' ? stats.reservedCoursesCount : undefined,
      hasQuizDone: typeof stats.hasQuizDone === 'boolean' ? stats.hasQuizDone : undefined,
      performancePercentile:
        typeof stats.performancePercentile === 'number' ? stats.performancePercentile : null,
    }
  }

  const studentStats = getStudentStats()

  return (
    <>
      <GenericModal
        title="Confirmer l'annulation"
        description={
          subscription?.status === 'ACTIVE'
            ? 'Vous pourrez annuler jusqu’à 12h avant le cours. Passé ce délai ou en cas d’absence, vous devrez attendre 48h avant de pouvoir réserver à nouveau'
            : "Vous pouvez annuler jusqu'à 12h avant le cours. Passé ce délai, le cours sera compté comme utilisé.'"
        }
        width={500}
        shouldHideCloseBtn
        open={presenter.showDeletionModal}
        onOpenChange={presenter.setShowDeletionModal}
        onCancel={presenter.handleConfirmCancelReservation}
        onCancelText="Garder le rendez-vous"
        onConfirmText="Annuler la réservation"
        onConfirm={presenter.handleCancelReservation}
      />

      {
        <div className="mx-auto min-h-[500px] max-w-7xl px-4 py-8">
          {presenter.incomingStudentReservations.length > 0 &&
            subscription?.status === 'ACTIVE' && (
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                <span className="font-semibold">Information :</span>Vous avez déjà un cours réservé.
                Vous ne pouvez pas réserver un autre cours tant que celui-ci n’est pas terminé.
              </div>
            )}
          <div className="mt-5 pb-5">
            <div className="mb-1 text-2xl font-bold text-gray-900">
              {presenter.getGreetingMessage()}
              <span className="ml-2">👋</span>
            </div>
            <div className="text-base text-gray-500">
              Prêt à continuer votre apprentissage aujourd&apos;hui ?
            </div>
          </div>
          <div className="mt-5 grid min-h-[500px] w-full grid-cols-1 gap-8 md:grid-cols-3">
            {/* Colonne principale (gauche, 2/3) */}
            <div className="flex flex-col gap-6 md:col-span-2 md:max-w-none">
              {/* Prochaine séance ou message */}
              <div>
                <StudentCoursesCard
                  incomingStudentReservations={presenter.incomingStudentReservations}
                  previousStudentReservations={presenter.previousStudentReservations}
                  setReservationToDeleteId={presenter.setReservationToDeleteId}
                  setShowDeletionModal={presenter.setShowDeletionModal}
                  navigate={presenter.navigate}
                  nextRecommendation={presenter.nextRecommendation}
                  isRecommendationLoading={presenter.isLoading}
                />
              </div>
              {presenter.incomingStudentReservations.length > 0 && (
                <div>
                  <StudentProgressOverview
                    nextRecommendation={presenter.nextRecommendation}
                    isLoading={presenter.isLoading}
                  />
                </div>
              )}
            </div>
            {hasShowPopUp && (
              <FreeLessonModal
                open={hasShowPopUp}
                openChange={setHasShowPopUp}
                close={() => {
                  setHasShowPopUp(false)
                  const url = new URL(window.location.href)
                  url.searchParams.delete('onboarded')
                  window.history.replaceState({}, document.title, url.toString())
                }}
                confirm={() => {
                  const url = new URL(window.location.href)
                  url.searchParams.delete('onboarded')
                  window.history.replaceState({}, document.title, url.toString())
                  const grade = student?.grade?.toLowerCase()
                  const { moduleId, chapterId } = testSession ?? {}
                  void navigate({
                    to: `/ressources/${grade}/maths/${moduleId}/${chapterId}/?book=true`,
                  })
                }}
              />
            )}
            <ViastudRulesModal open={isRulesModalOpen} openChange={setIsRulesModalOpen} />
            {/* Sidebar (droite, 1/3) */}
            <div className="flex flex-col gap-6 md:col-span-1 md:max-w-sm">
              <div className="rounded-lg bg-blue-50 p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Règles Viastud</p>
                    <p className="text-xs">Consulte les bonnes pratiques</p>
                  </div>

                  <button
                    onClick={() => {
                      setIsRulesModalOpen(true)
                    }}
                    className="text-sm font-semibold text-blue-600 hover:underline"
                  >
                    Voir
                  </button>
                </div>
              </div>

              <div className="flex flex-col rounded-lg bg-white p-6 shadow">
                <div className="mb-3 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  <h3 className="text-base font-semibold">Ma progression sur les chapitres</h3>
                </div>
                {/* DATA FROM ENDPOINT */}
                {presenter.isLoadingStudentStats ? (
                  <div className="text-center text-gray-500">Chargement...</div>
                ) : (
                  <>
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500">
                        Progression des chapitres
                      </span>
                      <span className="text-sm font-semibold text-gray-700">
                        {/* Placeholder, you can replace with real chapter progress if available */}
                        {studentStats?.chapterProgress ?? '-'}%
                      </span>
                    </div>
                    <div className="mb-3 h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: `${studentStats?.chapterProgress ?? 0}%`,
                          background: '#1d4ed8', // ou la variable CSS exacte
                          transition: 'width 0.3s',
                        }}
                      />
                    </div>
                    <div className="mb-2 grid grid-cols-2 gap-y-1">
                      <div>
                        <div className="text-base font-bold leading-tight">
                          {studentStats?.averageQuizGrade !== null &&
                          studentStats?.averageQuizGrade !== undefined
                            ? `${Math.round(studentStats.averageQuizGrade)}%`
                            : '-'}
                        </div>
                        <div className="text-xs text-gray-500">Moyenne quiz</div>
                      </div>
                      <div>
                        <div className="text-base font-bold leading-tight">
                          {studentStats?.reservedCoursesCount ?? 0}
                        </div>
                        <div className="text-xs text-gray-500">Cours réservés</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <StudentDashboardOverviewCard
                recentActivities={presenter.recentActivities}
                navigate={presenter.navigate}
                moduleId={presenter.getCurrentModuleId() ?? 0}
              />
            </div>
          </div>
        </div>
      }
    </>
  )
}
